/**
 * DistrictDashboard — TN-IPDS District Procurement Overview
 *
 * State → Region → District → Taluk → Panchayat drill-down.
 * Metrics from Tier 1-2 fast-path queries.
 * CM directive alignment overlay (no political claims in narrative).
 *
 * API:
 *   GET /v1/tn-ipds/district-dashboard?level=state|region|district|taluk
 *       &parent_id=<id>
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../config/apiConfig';

// INR formatter — lakh/crore
function formatINR(value) {
  if (value == null || isNaN(value)) return '—';
  const n = Number(value);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

const LEVEL_LABELS = {
  state:    'Tamil Nadu',
  region:   'Region',
  district: 'District',
  taluk:    'Taluk',
  panchayat:'Panchayat',
};

const LEVEL_NEXT = {
  state: 'region',
  region: 'district',
  district: 'taluk',
  taluk: 'panchayat',
};

const s = {
  container: { fontFamily: 'sans-serif', maxWidth: 1100, margin: '0 auto', padding: 24 },
  title: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#666', marginBottom: 20 },
  breadcrumb: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
  breadcrumbItem: (active) => ({
    padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    cursor: active ? 'default' : 'pointer',
    background: active ? '#4f46e5' : '#f3f4f6',
    color: active ? '#fff' : '#374151',
    border: '1px solid ' + (active ? '#4f46e5' : '#d1d5db'),
  }),
  metricsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12, marginBottom: 24,
  },
  metricCard: {
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  metricValue: { fontSize: 22, fontWeight: 700, color: '#1a1a2e', lineHeight: 1 },
  metricLabel: { fontSize: 11, color: '#888', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.5px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', background: '#f3f4f6', color: '#555', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', verticalAlign: 'top' },
  drillBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', fontWeight: 700, fontSize: 13, textDecoration: 'underline' },
  cmBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#d1fae5', color: '#065f46' },
  riskBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#991b1b' },
  empty: { textAlign: 'center', padding: 48, color: '#aaa', fontSize: 14 },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 },
  cmSection: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 16, marginBottom: 20 },
  cmTitle: { fontSize: 14, fontWeight: 700, color: '#065f46', marginBottom: 8 },
};

const DistrictDashboard = ({ clientId, apiBase }) => {
  const base = apiBase || API_BASE || '/v1';

  // Drill-down state: array of { level, id, name }
  const [breadcrumb, setBreadcrumb] = useState([{ level: 'state', id: null, name: 'Tamil Nadu' }]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cmDirectives, setCmDirectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentLevel = breadcrumb[breadcrumb.length - 1];
  const nextLevel = LEVEL_NEXT[currentLevel.level];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        level: currentLevel.level,
        ...(currentLevel.id ? { parent_id: currentLevel.id } : {}),
        ...(clientId ? { client_id: clientId } : {}),
      });
      const [dashRes, cmRes] = await Promise.allSettled([
        fetch(`${base}/tn-ipds/district-dashboard?${params}`),
        fetch(`${base}/tn-ipds/cm-directives?active=true`),
      ]);

      if (dashRes.status === 'fulfilled' && dashRes.value.ok) {
        const data = await dashRes.value.json();
        setRows(data.rows || []);
        setSummary(data.summary || null);
      } else {
        setRows([]);
        setSummary(null);
      }

      if (cmRes.status === 'fulfilled' && cmRes.value.ok) {
        const cmData = await cmRes.value.json();
        setCmDirectives(cmData.directives || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [base, clientId, currentLevel.level, currentLevel.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const drillDown = (row) => {
    if (!nextLevel) return;
    setBreadcrumb(prev => [...prev, {
      level: nextLevel,
      id: row.id,
      name: row.name,
    }]);
  };

  const navigateTo = (idx) => {
    setBreadcrumb(prev => prev.slice(0, idx + 1));
  };

  return (
    <div style={s.container}>
      <h2 style={s.title}>🗺️ District Dashboard</h2>
      <p style={s.subtitle}>
        Procurement performance across Tamil Nadu. Drill down from state → region →
        district → taluk → panchayat. All values in INR (lakh/crore).
      </p>

      {error && <div style={s.error} role="alert">⚠ {error}</div>}

      {/* Breadcrumb */}
      <div style={s.breadcrumb} aria-label="Drill-down navigation">
        {breadcrumb.map((crumb, idx) => (
          <React.Fragment key={idx}>
            <span
              style={s.breadcrumbItem(idx === breadcrumb.length - 1)}
              onClick={() => idx < breadcrumb.length - 1 && navigateTo(idx)}
              role={idx < breadcrumb.length - 1 ? 'button' : undefined}
              tabIndex={idx < breadcrumb.length - 1 ? 0 : undefined}
              onKeyDown={(e) => e.key === 'Enter' && idx < breadcrumb.length - 1 && navigateTo(idx)}
            >
              {crumb.name}
            </span>
            {idx < breadcrumb.length - 1 && <span style={{ color: '#aaa' }}>›</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Summary metrics */}
      {summary && (
        <div style={s.metricsGrid}>
          {[
            { label: 'Total Tenders', value: summary.total_tenders?.toLocaleString() },
            { label: 'Awarded', value: summary.awarded?.toLocaleString() },
            { label: 'Open', value: summary.open_tenders?.toLocaleString() },
            { label: 'Total Contracted', value: formatINR(summary.total_contracted) },
            { label: 'MSME Vendors', value: summary.msme_vendors?.toLocaleString() },
            { label: 'Open Risk Cases', value: summary.open_risk_cases?.toLocaleString() },
            { label: 'Single-Bid Rate', value: summary.single_bid_rate_pct != null ? `${summary.single_bid_rate_pct}%` : '—' },
            { label: 'Avg Bid Window', value: summary.avg_bid_window_days != null ? `${summary.avg_bid_window_days} days` : '—' },
          ].map(m => (
            <div key={m.label} style={s.metricCard}>
              <div style={s.metricValue}>{m.value ?? '—'}</div>
              <div style={s.metricLabel}>{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* CM Directives alignment (non-political — shows KPI targets only) */}
      {cmDirectives.length > 0 && (
        <div style={s.cmSection}>
          <div style={s.cmTitle}>📜 Active CM Directive Targets (KPI Alignment)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {cmDirectives.map((d, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                <strong>{d.directive_theme}</strong>
                {d.kpi_target && <span style={{ color: '#065f46', marginLeft: 6 }}>Target: {d.kpi_target}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Drill-down table */}
      {loading ? (
        <div style={s.empty}>Loading {LEVEL_LABELS[currentLevel.level]} data…</div>
      ) : rows.length === 0 ? (
        <div style={s.empty}>No data available for this level.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>{LEVEL_LABELS[nextLevel || currentLevel.level] || 'Name'}</th>
              <th style={s.th}>Tenders</th>
              <th style={s.th}>Awarded</th>
              <th style={s.th}>Total Contracted</th>
              <th style={s.th}>MSME Vendors</th>
              <th style={s.th}>Risk Cases</th>
              {nextLevel && <th style={s.th}>Drill Down</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id || i}>
                <td style={s.td}><strong>{row.name}</strong></td>
                <td style={s.td}>{row.total_tenders?.toLocaleString() ?? '—'}</td>
                <td style={s.td}>{row.awarded?.toLocaleString() ?? '—'}</td>
                <td style={s.td}>{formatINR(row.total_contracted)}</td>
                <td style={s.td}>{row.msme_vendors?.toLocaleString() ?? '—'}</td>
                <td style={s.td}>
                  {row.open_risk_cases > 0
                    ? <span style={s.riskBadge}>{row.open_risk_cases}</span>
                    : <span style={s.cmBadge}>0</span>}
                </td>
                {nextLevel && (
                  <td style={s.td}>
                    <button style={s.drillBtn} onClick={() => drillDown(row)}>
                      View {LEVEL_LABELS[nextLevel]} →
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DistrictDashboard;
